import React, { useState, useEffect, useMemo } from 'react';
import { apiRoutes } from 'api_routes/api_routes';
import styled from 'styled-components';
import GridTemplate from 'templates/GridTemplate';
import Paragraph from 'components/atoms/Paragraph/Paragraph';
import DatePicker from 'components/molecules/DatePicker/DatePicker';
import { Button } from '@mui/material';
import Divider from '@mui/material/Divider';
import axiosInstance from 'api/axios';
import { theme } from 'theme/MainTheme';
import { useTranslation } from 'react-i18next';
import DocTable from 'components/molecules/DocTable/DocTable';

const StyledPageHeader = styled.div`
  margin: 25px 0 20px 0;
  display: flex;
`;

const DateWrapper = styled.div`
  padding-right: 4rem;
`;

const Pits = () => {
  const { t } = useTranslation();
  const [pit, setPit] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const getData = async () => {
    axiosInstance.get(`${apiRoutes.get_user_files}${localStorage.getItem('user')}`).then((res) => {
      const pitFiles = res.data.filter((file) => file.group.toLowerCase() === 'pit-ift');
      pitFiles.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPit(pitFiles);
    });
  };

  useEffect(() => {
    getData();
  }, []);

  function getFilteredList() {
    if (!selectedCompany && !selectedStartDate && !selectedEndDate && !selectedType) {
      return pit;
    }
    if (selectedStartDate && selectedEndDate && selectedCompany && selectedType) {
      return pit.filter(
        (item) =>
          new Date(item.date).getFullYear() >= selectedStartDate?.$y &&
          new Date(item.date).getFullYear() <= selectedEndDate?.$y &&
          item.company === selectedCompany &&
          item.type === selectedType,
      );
    }
    if (selectedStartDate && selectedEndDate && selectedCompany && !selectedType) {
      return pit.filter(
        (item) =>
          new Date(item.date).getFullYear() >= selectedStartDate?.$y &&
          new Date(item.date).getFullYear() <= selectedEndDate?.$y &&
          item.company === selectedCompany,
      );
    }
    if (selectedStartDate && selectedEndDate && !selectedCompany && selectedType) {
      return pit.filter(
        (item) =>
          new Date(item.date).getFullYear() >= selectedStartDate?.$y &&
          new Date(item.date).getFullYear() <= selectedEndDate?.$y &&
          item.type === selectedType,
      );
    }
    if (selectedStartDate && selectedEndDate && !selectedCompany && !selectedType) {
      return pit.filter(
        (item) =>
          new Date(item.date).getFullYear() >= selectedStartDate?.$y &&
          new Date(item.date).getFullYear() <= selectedEndDate?.$y,
      );
    }
    if (selectedCompany && selectedType) {
      return pit.filter((item) => item.company === selectedCompany && item.type === selectedType);
    }
    if (selectedCompany && !selectedStartDate) {
      return pit.filter((item) => item.company === selectedCompany);
    }
    if (selectedType && !selectedStartDate) {
      return pit.filter((item) => item.type === selectedType);
    }
  }

  const filteredList = useMemo(getFilteredList, [
    selectedCompany,
    selectedType,
    selectedStartDate,
    selectedEndDate,
    pit,
  ]);

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleStartDateChange = (newValue) => {
    setSelectedStartDate(newValue);
  };

  const handleEndDateChange = (newValue) => {
    setSelectedEndDate(newValue);
  };

  const clearFilters = () => {
    setSelectedCompany('');
    setSelectedType('');
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  return (
    <GridTemplate pageType="PIT/IFT">
      <StyledPageHeader>
        <DateWrapper>
          <Paragraph>{t('date_filter')}</Paragraph>
          <DatePicker
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            views={['year']}
            label={t('year')}
          />
        </DateWrapper>
      </StyledPageHeader>
      <Button
        type="button"
        variant="contained"
        size="small"
        sx={{ backgroundColor: theme.primary, mb: 1, width: '90px' }}
        onClick={clearFilters}
      >
        {t('clear_filters')}
      </Button>
      <Divider />
      <DocTable
        listType={filteredList}
        company={selectedCompany}
        companyChange={handleCompanyChange}
        type={selectedType}
        typeChange={handleTypeChange}
        allItems={pit}
      />
    </GridTemplate>
  );
};

export default Pits;
